import { useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router";
import { formatMinor } from "@shared/money.ts";
import { bookingDisplayTitle } from "@shared/upcoming.ts";
import { humanStatus, UNKNOWN_FACT } from "@shared/labels.ts";
import { formatDateLabel, formatLongDate } from "@shared/time.ts";
import {
  bookingDiaryDates,
  previewBookingRemoval,
  removeBookingFromTrip,
} from "@shared/plan-lifecycle.ts";
import { presentBookingMoney, parseHouseholdView } from "@shared/money-view.ts";
import { useSession } from "../../app/session.tsx";
import { useTrip } from "../../app/trip.tsx";
import { moneyLabel } from "../../lib/format.ts";
import { FactRow } from "../../components/FactRow.tsx";
import { DocumentList } from "../../components/DocumentList.tsx";
import { PlaceActions } from "../../components/PlaceActions.tsx";
import { SmartBack } from "../../components/SmartBack.tsx";
import { filesFor } from "../../lib/records.ts";
import { readHouseholdView } from "../../lib/household-view.ts";
import { PaymentForm, RefundForm, SettlementForm } from "../money/PaymentForms.tsx";
import { StatusChip } from "../../components/StatusChip.tsx";
import { BookingEditForm } from "./BookingEditForm.tsx";
import { RemoveRecord } from "../../components/RemoveRecord.tsx";

type KindFilter = "all" | "hotel_reservation" | "transfer_package" | "excursion" | "flight";

export function BookingsPage() {
  const { trip, loading } = useTrip();
  const { role } = useSession();
  const [params, setParams] = useSearchParams();
  const household = (params.get("household") === "chris" || params.get("household") === "paul" ? params.get("household") : "all") as "all" | "chris" | "paul";
  const kind = (["all", "hotel_reservation", "transfer_package", "excursion", "flight"] as const).includes(params.get("kind") as KindFilter)
    ? (params.get("kind") as KindFilter)
    : "all";
  const query = params.get("q") ?? "";
  function setFilter(next: { household?: typeof household; kind?: KindFilter; q?: string }) {
    const url = new URLSearchParams(params);
    const nextHousehold = next.household ?? household;
    const nextKind = next.kind ?? kind;
    const nextQuery = next.q ?? query;
    if (nextHousehold === "all") url.delete("household");
    else url.set("household", nextHousehold);
    if (nextKind === "all") url.delete("kind");
    else url.set("kind", nextKind);
    if (!nextQuery.trim()) url.delete("q");
    else url.set("q", nextQuery);
    setParams(url, { replace: true });
  }
  if (!trip && loading) return <main className="page"><div className="skeleton" style={{ height: 160 }} /></main>;
  if (!trip) return <main className="page">Loading bookings…</main>;
  const bookings = trip.bookings.filter((b) => {
    if (household !== "all" && b.householdId !== household && !b.allocations.some((a) => a.householdId === household)) return false;
    if (kind !== "all" && b.kind !== kind) return false;
    if (query.trim()) {
      const blob = `${bookingDisplayTitle(b, trip)} ${b.confirmation ?? ""} ${b.provider ?? ""}`.toLowerCase();
      if (!blob.includes(query.trim().toLowerCase())) return false;
    }
    return true;
  });

  return (
    <main className="page">
      <header className="topbar">
        <h1 className="page-title">Bookings</h1>
        {role === "owner" ? <Link className="btn-secondary" to="/bookings/add">Add plan</Link> : null}
      </header>
      <p className="muted">Household filters change the view only. Money lives under Money.</p>
      <div className="chip-row">
        {(["all", "chris", "paul"] as const).map((id) => (
          <button key={id} className={`chip ${household === id ? "selected" : ""}`} type="button" aria-pressed={household === id} onClick={() => setFilter({ household: id })}>
            {id === "all" ? "All" : trip.households.find((h) => h.id === id)?.label}
          </button>
        ))}
      </div>
      <div className="chip-row" style={{ marginTop: 8 }}>
        {([
          ["all", "All types"],
          ["hotel_reservation", "Hotels"],
          ["transfer_package", "Transfers"],
          ["excursion", "Excursions"],
          ["flight", "Flights"],
        ] as const).map(([id, label]) => (
          <button key={id} className={`chip ${kind === id ? "selected" : ""}`} type="button" aria-pressed={kind === id} onClick={() => setFilter({ kind: id })}>
            {label}
          </button>
        ))}
      </div>
      <label className="field">
        Find in this list
        <input value={query} onChange={(e) => setFilter({ q: e.target.value })} type="search" />
      </label>
      <div className="timeline">
        {bookings.map((booking) => {
          const diary = bookingDiaryDates(trip, booking);
          const range = diary.startDate
            ? (diary.endDate && diary.endDate !== diary.startDate
              ? `${formatDateLabel(diary.startDate)} - ${formatDateLabel(diary.endDate)}`
              : formatDateLabel(diary.startDate))
            : null;
          return (
            <Link key={booking.id} to={`/bookings/${booking.id}${params.toString() ? `?${params.toString()}` : ""}`} className="card" style={{ color: "inherit", textDecoration: "none" }}>
              <p className="kicker">{humanStatus(booking.kind)}</p>
              <h2 className="serif" style={{ margin: 0 }}>{bookingDisplayTitle(booking, trip)}</h2>
              <p>{humanStatus(booking.bookingStatus)} · {moneyLabel(booking.cost, UNKNOWN_FACT)}</p>
              {range ? <p className="muted">{range}</p> : null}
            </Link>
          );
        })}
      </div>
    </main>
  );
}

export function BookingDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { trip, refresh, save, saveState } = useTrip();
  const { role } = useSession();
  const [params] = useSearchParams();
  const [sheet, setSheet] = useState<"pay" | "settle" | "refund" | "edit" | null>(
    params.get("edit") === "1" ? "edit" : null,
  );
  const [removeError, setRemoveError] = useState<string | null>(null);
  useEffect(() => {
    if (sheet !== "edit" || !trip) return;
    document.getElementById("edit-amounts")?.scrollIntoView({ block: "start", behavior: "smooth" });
  }, [sheet, trip]);
  const booking = trip?.bookings.find((b) => b.id === id);
  if (!trip || !booking) return <main className="page"><SmartBack fallback="/bookings" /><p>Booking not found.</p></main>;
  const view = parseHouseholdView(params.get("view") ?? readHouseholdView(), trip.households);
  const files = filesFor(trip, booking.id, booking.stayId);
  const stay = trip.stays.find((s) => s.id === booking.stayId);
  const place = stay ? trip.places.find((p) => p.id === stay.placeId) : undefined;
  const money = presentBookingMoney(trip, booking.id);
  const flight = trip.flights.find((f) => booking.title?.includes(f.number) || (booking.confirmation && f.pnr === booking.confirmation));
  const fromMoney = window.location.pathname.startsWith("/money/");
  const viewQuery = view === "all" ? "" : `?view=${view}`;
  const bookingsBack = (() => {
    const next = new URLSearchParams();
    for (const key of ["household", "kind", "q"] as const) {
      const value = params.get(key);
      if (value) next.set(key, value);
    }
    const qs = next.toString();
    return qs ? `/bookings?${qs}` : "/bookings";
  })();
  const diary = bookingDiaryDates(trip, booking);
  const removal = previewBookingRemoval(trip, booking.id);
  const linkedPlans = trip.events.filter((event) => event.bookingId === booking.id && !event.archived);

  return (
    <main className="page money-page">
      <SmartBack fallback={fromMoney ? `/money${viewQuery}` : bookingsBack} />
      <p className="kicker">{humanStatus(booking.kind)}</p>
      <h1 className="page-title">{bookingDisplayTitle(booking, trip)}</h1>
      <p><StatusChip status={booking.bookingStatus} /></p>
      {booking.kind === "hotel_reservation" && stay && place ? (
        <>
          {role === "owner" ? (
            <p><Link className="btn-secondary" to={`/journey/stay/${stay.id}/edit`}>Edit stay details</Link></p>
          ) : null}
          <p>
            {formatDateLabel(booking.startDate ?? stay.startDate)} - {formatDateLabel(booking.endDate ?? stay.endDate)}
            {booking.householdId ? ` · ${trip.households.find((h) => h.id === booking.householdId)?.label}` : ""}
          </p>
          {place.address ? <p>{place.address}</p> : null}
          <PlaceActions place={place} />
          <dl>
            <FactRow label="Room" value={booking.roomName} />
            <FactRow label="Confirmation" value={booking.confirmation} copyable />
            <FactRow label="Agent itinerary" value={booking.expediaItinerary} copyable />
            <FactRow label="Guests" value={booking.occupancy ? `${booking.occupancy.adults} adults, ${booking.occupancy.children} children` : null} />
            <FactRow label="Check-in from" value={stay.checkInFrom} />
            <FactRow label="Free cancellation until" value={booking.freeCancellationUntil} />
            <FactRow label="Cancellation terms" value={booking.cancellationTerms} />
          </dl>
          {booking.notes.map((note) => <p key={note}>{note}</p>)}
          {booking.breakfastIncluded ? <p>Breakfast included</p> : null}
          {booking.beverageCredit ? <p className="muted">{moneyLabel(booking.beverageCredit)} credit is a benefit, not subtracted.</p> : null}
          <Link to={`/journey/stay/${stay.id}`}>Open the stay</Link>
        </>
      ) : null}
      {booking.kind === "transfer_package" ? (
        <>
          <p>{booking.provider}{booking.vehicle ? ` · ${booking.vehicle}` : ""}</p>
          {diary.startDate ? (
            <p>
              {diary.endDate && diary.endDate !== diary.startDate
                ? `${formatLongDate(diary.startDate)} and ${formatLongDate(diary.endDate)}`
                : formatLongDate(diary.startDate)}
            </p>
          ) : null}
          {linkedPlans.map((event) => (
            <div key={event.id} className="row-actions">
              <Link to={`/journey/transfer/${event.id}`}>{event.pickupInstruction ?? event.title ?? "Open pickup"}</Link>
              {role === "owner" ? (
                <Link className="btn-secondary" to={`/journey/day/${event.date}/add?edit=${event.id}`}>Change day</Link>
              ) : null}
            </div>
          ))}
        </>
      ) : null}
      {booking.kind === "excursion" ? (
        <>
          {diary.startDate ? <p>{formatLongDate(diary.startDate)}</p> : null}
          {linkedPlans.map((event) => (
            <div key={event.id} className="row-actions">
              <Link to={`/journey/event/${event.id}`}>Open the plan</Link>
              {role === "owner" ? (
                <Link className="btn-secondary" to={`/journey/day/${event.date}/add?edit=${event.id}`}>Change day</Link>
              ) : null}
            </div>
          ))}
          {booking.priceBasis && booking.cost ? (
            <p>
              {booking.priceBasis.adultCount} adults × {formatMinor(booking.cost.currency, booking.priceBasis.adultAmountMinor)}
              {booking.priceBasis.paidChildCount ? ` · ${booking.priceBasis.paidChildCount} child × ${formatMinor(booking.cost.currency, booking.priceBasis.childAmountMinor)}` : ""}
              {booking.priceBasis.freeChildCount ? ` · ${booking.priceBasis.freeChildCount} free` : ""}
            </p>
          ) : null}
          {booking.dietaryNotes?.map((note) => <p key={note}>{note}</p>)}
        </>
      ) : null}
      {booking.kind === "flight" && flight ? (
        <p><Link className="btn" to={`/journey/flight/${flight.id}`}>Open the flight card</Link></p>
      ) : null}

      {sheet === "edit" && role === "owner" ? (
        <div id="edit-amounts">
          <BookingEditForm
            trip={trip}
            booking={booking}
            saving={saveState === "saving"}
            onSave={async (next) => {
              const result = await save(next);
              if (result.ok) setSheet(null);
              return result;
            }}
          />
        </div>
      ) : null}

      {money ? (
        <section className="money-summary">
          <h2 className="section-title">Cost and payment</h2>
          <p className="money-figure"><span className="amount-pair tabular">{moneyLabel(booking.cost, UNKNOWN_FACT)}</span></p>
          {booking.allocations.map((a) => (
            <p key={a.householdId}>
              {trip.households.find((h) => h.id === a.householdId)?.label ?? a.householdId} share: {booking.cost ? formatMinor(booking.cost.currency, a.amountMinor) : ""}
            </p>
          ))}
          {booking.allocationStatus ? <p className="muted">{humanStatus(booking.allocationStatus)}</p> : null}
          {booking.cost ? (
            <p>Supplier remaining {formatMinor(booking.cost.currency, money.ledger.supplierRemaining)}</p>
          ) : null}
          {money.originalCovered.length ? (
            <p>
              Originally covered on this booking: {money.originalCovered.map((c) =>
                `${trip.households.find((h) => h.id === c.toHouseholdId)?.label} paid ${trip.households.find((h) => h.id === c.fromHouseholdId)?.label}'s ${formatMinor(c.currency, c.amountMinor)}`
              ).join(". ")}.
            </p>
          ) : <p>No one has funded the other household on this booking.</p>}
          {money.currentNet.length === 0 ? (
            <p><strong>Current household position: nothing to repay.</strong> Settlements are trip-wide, not invented onto this booking.</p>
          ) : (
            <p>
              Current household position: {money.currentNet.map((c) =>
                `${trip.households.find((h) => h.id === c.fromHouseholdId)?.label} owes ${trip.households.find((h) => h.id === c.toHouseholdId)?.label} ${formatMinor(c.currency, c.amountMinor)}`
              ).join(". ")}.
            </p>
          )}
          {money.currentCredits.length ? (
            <p>
              Credit still to apply: {money.currentCredits.map((c) =>
                `${formatMinor(c.currency, c.amountMinor)} from ${trip.households.find((h) => h.id === c.fromHouseholdId)?.label} to ${trip.households.find((h) => h.id === c.toHouseholdId)?.label}`
              ).join(". ")}.
            </p>
          ) : null}
          {money.ledger.fundingReversalIncomplete ? (
            <p className="banner warn">A refund is recorded without a funding reversal. Household shares may not match the supplier balance until that is completed.</p>
          ) : null}
          {money.historicalBaseline ? <p className="muted">{money.historicalBaseline}. Current state comes from recorded payments.</p> : null}
          <h3 className="section-title">History</h3>
          {money.payments.length === 0 && money.refunds.length === 0 ? <p className="muted">No payments recorded yet.</p> : null}
          {money.payments.map((p) => (
            <p key={p.id} className="pay-row">
              {p.date} · {trip.households.find((h) => h.id === p.payerHouseholdId)?.label} paid {formatMinor(p.credit.currency, p.credit.amountMinor)}
              {p.actualCharge ? ` · card ${formatMinor(p.actualCharge.currency, p.actualCharge.amountMinor)}` : ""}
            </p>
          ))}
          {money.refunds.map((r) => (
            <p key={r.id} className="pay-row">Refund {r.date} · {formatMinor(r.credit.currency, r.credit.amountMinor)}</p>
          ))}
          {role === "owner" ? (
            <div className="row-actions">
              <button className="btn" type="button" onClick={() => setSheet("pay")}>Record payment</button>
              <button className="btn-secondary" type="button" onClick={() => setSheet("settle")}>Record reimbursement</button>
              {money.payments.length ? <button className="btn-ghost" type="button" onClick={() => setSheet("refund")}>Record refund</button> : null}
              {booking.kind !== "flight" ? <button className="btn-secondary" type="button" onClick={() => setSheet("edit")}>Edit amounts</button> : null}
            </div>
          ) : null}
        </section>
      ) : null}

      {sheet === "pay" ? <PaymentForm initialBookingId={booking.id} onSaved={() => { setSheet(null); void refresh(); }} /> : null}
      {sheet === "settle" ? <SettlementForm onSaved={() => { setSheet(null); void refresh(); }} /> : null}
      {sheet === "refund" ? <RefundForm bookingId={booking.id} onSaved={() => { setSheet(null); void refresh(); }} /> : null}

      {role === "owner" && !("error" in removal) ? (
        <RemoveRecord
          preview={removal}
          busy={saveState === "saving"}
          error={removeError}
          onConfirm={() => {
            const result = removeBookingFromTrip(trip, booking.id);
            if (!result.ok) {
              setRemoveError(result.error);
              return;
            }
            void save(result.doc).then((saved) => {
              if (!saved.ok) {
                setRemoveError(saved.message);
                return;
              }
              navigate("/bookings");
            });
          }}
        />
      ) : null}

      <DocumentList entityId={booking.id} files={files} />
    </main>
  );
}
